"use client";

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, IconButton, Alert, CircularProgress, Tooltip } from '@mui/material';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { colors, typography } from '@/theme/colors';
interface CreateKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
type ModalState = 'form' | 'loading' | 'success';
interface CreatedKey {
  id: number;
  name: string;
  prefix: string;
  key: string;
  created_at: string;
}
const CreateKeyModal: React.FC<CreateKeyModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const t = useTranslations('apiKeys');
  const [state, setState] = useState<ModalState>('form');
  const [keyName, setKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleCreate = async () => {
    setState('loading');
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && {
            Authorization: `Bearer ${token}`
          })
        },
        body: JSON.stringify({
          name: keyName || 'My API Key'
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || data.message || 'Failed to create key');
      }
      const data = await res.json();
      setCreatedKey(data);
      setState('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
      setState('form');
    }
  };
  const handleCopy = async () => {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const handleDone = () => {
    onSuccess();
    // Reset state for next open
    setState('form');
    setKeyName('');
    setCreatedKey(null);
    setCopied(false);
    setError(null);
  };
  const handleClose = () => {
    if (state === 'loading') return;
    if (state === 'success') {
      handleDone();
    } else {
      onClose();
      // Reset state
      setState('form');
      setKeyName('');
      setError(null);
    }
  };
  return <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
    sx: {
      borderRadius: '12px'
    }
  }} slotProps={{
    backdrop: {
      sx: {
        transition: 'opacity 0.2s ease'
      }
    }
  }} data-sentry-element="Dialog" data-sentry-component="CreateKeyModal" data-sentry-source-file="CreateKeyModal.tsx">
      <DialogTitle sx={{
      ...typography.presets.sectionTitle
    }} data-sentry-element="DialogTitle" data-sentry-source-file="CreateKeyModal.tsx">
        {t('createKey')}
      </DialogTitle>

      <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="CreateKeyModal.tsx">
        {state === 'form' && <Box sx={{
        pt: 1
      }}>
            {error && <Alert severity="error" sx={{
          mb: 2,
          borderRadius: '8px'
        }}>
                {error}
              </Alert>}

            <Typography sx={{
          ...typography.presets.bodySecondary,
          color: colors.text.primary,
          mb: 1
        }}>
              {t('keyNameLabel')}
            </Typography>
            <TextField fullWidth placeholder={t('keyNamePlaceholder')} value={keyName} onChange={e => setKeyName(e.target.value)} sx={{
          mb: 2
        }} />
          </Box>}

        {state === 'loading' && <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4
      }}>
            <CircularProgress />
          </Box>}

        {state === 'success' && createdKey && <Box sx={{
        pt: 1,
        animation: 'scaleIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
        '@keyframes scaleIn': {
          from: {
            opacity: 0,
            transform: 'scale(0.95)'
          },
          to: {
            opacity: 1,
            transform: 'scale(1)'
          }
        }
      }}>
            <Alert severity="warning" sx={{
          mb: 3,
          borderRadius: '8px'
        }}>
              {t('keyWarning')}
            </Alert>

            <Typography sx={{
          ...typography.presets.caption,
          color: colors.text.tertiary,
          mb: 1
        }}>
              Your API Key:
            </Typography>

            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: colors.background.light,
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${colors.border.light}`
        }}>
              <Typography sx={{
            fontFamily: 'monospace',
            fontSize: '14px',
            color: colors.text.primary,
            wordBreak: 'break-all',
            flex: 1
          }}>
                {createdKey.key}
              </Typography>

              <Tooltip title={copied ? t('copySuccess') : 'Copy'}>
                <IconButton onClick={handleCopy} sx={{
              minWidth: '44px',
              minHeight: '44px',
              padding: '10px',
              transition: 'all 0.15s ease',
              '&:active': {
                transform: 'scale(0.95)'
              }
            }}>
                  {copied ? <CheckIcon sx={{
                color: colors.status.success.main
              }} /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>}
      </DialogContent>

      <DialogActions sx={{
      px: 3,
      pb: 3,
      gap: 1
    }} data-sentry-element="DialogActions" data-sentry-source-file="CreateKeyModal.tsx">
        {state === 'form' && <>
            <Button onClick={handleClose} sx={{
          textTransform: 'none',
          minWidth: '44px',
          minHeight: '44px',
          color: colors.text.tertiary
        }}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCreate} variant="contained" sx={{
          textTransform: 'none',
          minWidth: '44px',
          minHeight: '44px',
          bgcolor: colors.primary.main,
          transition: 'all 0.15s ease',
          '&:hover': {
            bgcolor: colors.primary.hover
          },
          '&:active': {
            transform: 'scale(0.97)'
          }
        }}>
              {t('createKey')}
            </Button>
          </>}

        {state === 'success' && <Button onClick={handleDone} variant="contained" sx={{
        textTransform: 'none',
        minWidth: '44px',
        minHeight: '44px',
        bgcolor: colors.primary.main,
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: colors.primary.hover
        },
        '&:active': {
          transform: 'scale(0.97)'
        }
      }}>
            {t('done')}
          </Button>}
      </DialogActions>
    </Dialog>;
};
export default CreateKeyModal;